import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '@/lib/api'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useFeatureGate } from '@/hooks/useFeatureGate'
import { Loader2, CreditCard, AlertCircle, RefreshCw, CheckCircle2, Sparkles, Phone, MessageCircle, Receipt, ShieldCheck } from 'lucide-react'
import { UpdatePaymentMethodModal } from '@/components/billing/UpdatePaymentMethodModal'
import { InvoicesList } from '@/components/billing/InvoicesList'
import { CancelSubscriptionDialog } from '@/components/billing/CancelSubscriptionDialog'
import { ChangePlanModal } from '@/components/billing/ChangePlanModal'
import { ConvertDemoDialog } from '@/components/billing/ConvertDemoDialog'

interface SubscriptionState {
  tier: string
  status: string
  trialEndsAt?: string
  // Set when the org was created via access-code redemption. Used to render
  // a distinct "Demo · N days left" state instead of the trial countdown.
  demoExpiresAt?: string | null
  stripeCustomerId?: string
  stripeSubscriptionId?: string
  pendingTier?: string | null
  pendingEffectiveAt?: string | null
  stripe?: {
    status: string
    currentPeriodEnd: string
    cancelAtPeriodEnd: boolean
    trialEnd: string | null
  }
}

interface PaymentMethod {
  brand: string
  last4: string
  expMonth: number
  expYear: number
}

interface UsageState {
  // included: null = unlimited, 0 = not on this tier, number = base allowance.
  // Doesn't yet account for purchased call packs — credit tracking lands with
  // UsageService (see /api/billing/usage backend comment).
  calls: { used: number; included: number | null }
  messages: { used: number; included: number | null }
  period: { start: string; end: string }
}

type CallPackSlug =
  | 'callpack_10'
  | 'callpack_25'
  | 'callpack_50'
  | 'callpack_100'
  | 'callpack_250'
  | 'callpack_500'
  | 'callpack_test_1'

const CALL_PACKS: Array<{ slug: CallPackSlug; calls: number; price: number; testOnly?: boolean }> = [
  { slug: 'callpack_10', calls: 10, price: 20 },
  { slug: 'callpack_25', calls: 25, price: 45 },
  { slug: 'callpack_50', calls: 50, price: 80 },
  { slug: 'callpack_100', calls: 100, price: 150 },
  { slug: 'callpack_250', calls: 250, price: 350 },
  { slug: 'callpack_500', calls: 500, price: 650 },
  // Platform-admin-only $0 dry-run. Backend skips Stripe entirely so the
  // confirmation dialog and end-to-end wiring can be verified without a
  // real charge.
  { slug: 'callpack_test_1', calls: 1, price: 0, testOnly: true },
]

const TIER_LABELS: Record<string, string> = {
  associate: 'Associate',
  partner: 'Partner',
  principal: 'Principal',
  trial: 'Trial',
}

function daysBetween(from: Date, to: Date): number {
  return Math.max(0, Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)))
}

function cardBrandLabel(brand: string): string {
  const map: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay',
  }
  return map[brand] ?? brand.charAt(0).toUpperCase() + brand.slice(1)
}

export default function BillingPage() {
  const navigate = useNavigate()
  const [sub, setSub] = useState<SubscriptionState | null>(null)
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null)
  const [usage, setUsage] = useState<UsageState | null>(null)
  const [loading, setLoading] = useState(true)
  const [packBuying, setPackBuying] = useState<CallPackSlug | null>(null)
  const [reactivating, setReactivating] = useState(false)

  const [updateCardOpen, setUpdateCardOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  // Mobile-only inline modals — on phones we open the popup right here
  // instead of routing to /settings/billing/plans, since the page-level
  // tier comparison and add-ons grid is heavy for a small screen.
  const [changePlanOpen, setChangePlanOpen] = useState(false)
  const [convertOpen, setConvertOpen] = useState(false)
  // Purchase confirmation: holds the pack the user clicked, awaiting Confirm.
  // null = no dialog open. Charge only fires after explicit confirmation.
  const [pendingPack, setPendingPack] = useState<typeof CALL_PACKS[number] | null>(null)

  const { toast } = useToast()
  const { isPlatformAdmin, isSuperAdmin } = useFeatureGate()
  const visiblePacks = CALL_PACKS.filter(p => !p.testOnly || isPlatformAdmin || isSuperAdmin)

  useEffect(() => {
    loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function loadAll() {
    setLoading(true)
    try {
      const [subRes, pmRes, usageRes] = await Promise.all([
        api.get('/api/billing/subscription'),
        api.get('/api/billing/payment-method').catch(() => ({ data: { data: null } })),
        // Usage failure shouldn't blank the whole billing page — degrade gracefully.
        api.get('/api/billing/usage').catch(() => ({ data: { data: null } })),
      ])
      setSub(subRes.data.data)
      setPaymentMethod(pmRes.data.data)
      setUsage(usageRes.data.data)
    } catch (err: any) {
      toast({
        title: 'Failed to load billing',
        description: err.response?.data?.message ?? err.message,
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  async function buyCallPack(slug: CallPackSlug) {
    setPackBuying(slug)
    try {
      // confirmed: true is required by the API as a server-side guard —
      // a stale frontend bundle without this flag gets rejected instead of
      // charging. We only send true after the user has clicked the confirm
      // button in the dialog (this function is only called from there).
      const res = await api.post('/api/billing/call-pack/buy', { packSlug: slug, confirmed: true })
      const isTest = res.data?.data?.test
      toast({
        title: isTest ? 'Test purchase OK' : 'Call Pack added',
        description: isTest
          ? 'Confirmation flow verified — no charge was made.'
          : `${res.data.data.callsAdded} calls added to your bucket.`,
      })
    } catch (err: any) {
      toast({
        title: 'Purchase failed',
        description: err.response?.data?.message ?? err.message,
        variant: 'destructive',
      })
    } finally {
      setPackBuying(null)
    }
  }

  async function reactivate() {
    setReactivating(true)
    try {
      await api.post('/api/billing/subscription/reactivate')
      toast({
        title: 'Reactivated',
        description: 'Your subscription will continue as normal.',
      })
      await loadAll()
    } catch (err: any) {
      toast({
        title: 'Failed to reactivate',
        description: err.response?.data?.message ?? err.message,
        variant: 'destructive',
      })
    } finally {
      setReactivating(false)
    }
  }

  async function cancelPendingChange() {
    try {
      await api.post('/api/billing/subscription/cancel-pending-change')
      toast({
        title: 'Scheduled change cancelled',
        description: `You'll stay on your current ${TIER_LABELS[sub?.tier ?? ''] ?? sub?.tier} plan.`,
      })
      await loadAll()
    } catch (err: any) {
      toast({
        title: 'Failed to cancel',
        description: err.response?.data?.message ?? err.message,
        variant: 'destructive',
      })
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading billing…
      </div>
    )
  }

  if (!sub) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6">
          <AlertCircle className="h-5 w-5 text-destructive mb-2" />
          <p className="text-sm text-destructive">Could not load subscription. Please try again.</p>
        </div>
      </div>
    )
  }

  // Either an access-code demo (subscription_status='demo_active') OR a legacy
  // trial that never had a Stripe customer created. Both need to go through
  // checkout to create the Stripe customer + subscription — the regular
  // update-card / change-plan modals assume a customer already exists and
  // fail with "No Stripe customer" otherwise.
  // "Needs to subscribe" rather than "needs to switch tiers". Covers:
  //  - Access-code demos (status='demo_active')
  //  - Legacy trials with no Stripe customer
  //  - Any state where there's no live Stripe subscription to mutate —
  //    the change-plan modal can't help these users (it tries to fetch
  //    a proration preview against a subscription that doesn't exist).
  const needsSubscribe =
    sub.status === 'demo_active' || !sub.stripeCustomerId || !sub.stripeSubscriptionId

  // Open the right popup on mobile, navigate to the polished plans page on
  // larger screens. The page is content-heavy (comparison table, add-ons,
  // call packs) — fine on desktop, overkill on a phone.
  const openPlans = () => {
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches
    if (isMobile) {
      if (needsSubscribe) setConvertOpen(true)
      else setChangePlanOpen(true)
    } else {
      navigate('/settings/billing/plans')
    }
  }

  const tierLabel = TIER_LABELS[sub.tier] ?? sub.tier
  const now = new Date()
  const trialEnd = sub.trialEndsAt ? new Date(sub.trialEndsAt) : null
  const trialDaysLeft = trialEnd ? daysBetween(now, trialEnd) : null
  const isTrialing = sub.status === 'trialing'
  const isPastDue = sub.status === 'past_due'
  const isDemoActive = sub.status === 'demo_active'
  const currentPeriodEnd = sub.stripe?.currentPeriodEnd ? new Date(sub.stripe.currentPeriodEnd) : null
  const cancelAtPeriodEnd = sub.stripe?.cancelAtPeriodEnd ?? false

  // Trial countdown ring math (used by both the hero card and the dedicated banner)
  const trialTotalDays = 14
  const trialDaysUsed = trialDaysLeft !== null ? Math.max(0, trialTotalDays - trialDaysLeft) : 0
  const trialPct = trialDaysLeft !== null ? Math.min(100, (trialDaysUsed / trialTotalDays) * 100) : 0
  const trialRingCirc = 2 * Math.PI * 26
  const trialRingDash = trialRingCirc * (1 - trialPct / 100)

  // Demo countdown uses demo_expires_at (set at access-code redemption).
  // We don't render a fill ring (the original duration_days isn't surfaced
  // to the dashboard, and approximating it would lie). Just show the
  // exact days-left in a static circle with the "Demo" badge for context.
  const demoEnd = sub.demoExpiresAt ? new Date(sub.demoExpiresAt) : null
  const demoDaysLeft = demoEnd ? daysBetween(now, demoEnd) : null

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-heading font-semibold tracking-tight">Billing & coverage</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your plan, payment method, and invoices.
        </p>
      </div>

      {/* HERO: Current plan card with gradient accent + inline trial countdown */}
      <div className="relative overflow-hidden rounded-2xl border bg-card shadow-sm card-inset">
        {/* Decorative gradient orb */}
        <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-gradient-to-br from-brand-periwinkle/25 via-brand-royal/15 to-transparent blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-32 -left-20 h-56 w-56 rounded-full bg-gradient-to-tr from-brand-royal/15 to-transparent blur-3xl" aria-hidden="true" />

        <div className="relative p-5 sm:p-7">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            {/* Plan info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Current plan</span>
                {isDemoActive ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                    <Sparkles className="h-3 w-3" />
                    Demo
                  </span>
                ) : isTrialing ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-brand-periwinkle/15 text-brand-periwinkle dark:text-brand-periwinkle px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-periwinkle animate-pulse" />
                    Trialing
                  </span>
                ) : isPastDue ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                    <AlertCircle className="h-3 w-3" />
                    Past due
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-500/15 text-green-600 dark:text-green-400 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                    <CheckCircle2 className="h-3 w-3" />
                    Active
                  </span>
                )}
              </div>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
                {tierLabel}
              </h2>
              {currentPeriodEnd && (
                <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-brand-periwinkle" />
                  {cancelAtPeriodEnd ? 'Coverage ends' : 'Renews'} on{' '}
                  <span className="font-semibold text-foreground">
                    {currentPeriodEnd.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                </p>
              )}

              <div className="flex flex-wrap gap-2 mt-5">
                <Button
                  onClick={openPlans}
                  className="bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90 shadow-md shadow-brand-periwinkle/20"
                >
                  {needsSubscribe ? 'See packages' : 'See all plans'}
                </Button>
                {!cancelAtPeriodEnd && sub.stripeSubscriptionId && (
                  <Button
                    variant="ghost"
                    onClick={() => setCancelOpen(true)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>

            {/* Demo countdown — shown when org is in demo_active. Uses a
                static circle (not a fill ring) since the original
                duration_days isn't plumbed to the frontend. */}
            {isDemoActive && demoDaysLeft !== null && (
              <div className="flex items-center gap-4 lg:gap-5 lg:pl-6 lg:border-l lg:border-border/60 shrink-0">
                <div className="relative h-20 w-20 shrink-0">
                  <div className="h-20 w-20 rounded-full border-[5px] border-emerald-400/40 bg-emerald-500/5 flex flex-col items-center justify-center">
                    <span className="text-xl font-bold tabular-nums text-emerald-700 dark:text-emerald-300">{demoDaysLeft}</span>
                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground -mt-0.5">days</span>
                  </div>
                </div>
                <div className="max-w-[200px]">
                  <p className="text-xs font-semibold text-foreground">Demo ends in {demoDaysLeft} day{demoDaysLeft === 1 ? '' : 's'}</p>
                  <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                    No card needed during your demo. Subscribe any time to keep going past day {demoDaysLeft === 0 ? '0' : demoDaysLeft}.
                  </p>
                </div>
              </div>
            )}

            {/* Trial countdown ring (only during trial) */}
            {isTrialing && trialDaysLeft !== null && (
              <div className="flex items-center gap-4 lg:gap-5 lg:pl-6 lg:border-l lg:border-border/60 shrink-0">
                <div className="relative h-20 w-20 shrink-0">
                  <svg className="h-20 w-20 -rotate-90" viewBox="0 0 60 60" aria-hidden="true">
                    <circle cx="30" cy="30" r="26" fill="none" strokeWidth="5" className="stroke-muted" />
                    <circle
                      cx="30"
                      cy="30"
                      r="26"
                      fill="none"
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeDasharray={trialRingCirc}
                      strokeDashoffset={trialRingDash}
                      className="stroke-brand-periwinkle transition-[stroke-dashoffset] duration-700"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-xl font-bold tabular-nums">{trialDaysLeft}</span>
                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground -mt-0.5">days</span>
                  </div>
                </div>
                <div className="max-w-[180px]">
                  <p className="text-xs font-semibold text-foreground">Trial ends in {trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'}</p>
                  <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                    {paymentMethod
                      ? `We'll charge your card and keep ${tierLabel} running.`
                      : 'Add a card to keep coverage past day 14.'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status banners (past_due, cancel scheduled, pending change) — hero
          handles the trialing state, so the dedicated trial banner is gone. */}

      {isPastDue && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" strokeWidth={2} />
          <div className="flex-1">
            <p className="text-sm font-medium">Payment issue on your account</p>
            <p className="text-xs text-muted-foreground mt-1">
              We couldn't process your last payment. Update your card to keep coverage running.
            </p>
            <Button size="sm" onClick={() => setUpdateCardOpen(true)} className="mt-3">
              Update payment method
            </Button>
          </div>
        </div>
      )}

      {cancelAtPeriodEnd && currentPeriodEnd && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" strokeWidth={2} />
          <div className="flex-1">
            <p className="text-sm font-medium">
              Cancellation scheduled for {currentPeriodEnd.toLocaleDateString()}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              You'll keep full access until then. Reactivate anytime before then and nothing changes.
            </p>
            <Button
              size="sm"
              onClick={reactivate}
              disabled={reactivating}
              className="mt-3 bg-gradient-to-r from-brand-royal to-brand-periwinkle"
            >
              {reactivating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Reactivating…
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reactivate subscription
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Pending (scheduled) downgrade banner — only shows when /subscription
          returns pendingTier + pendingEffectiveAt. Customer sees what they
          scheduled and can Cancel to stay on their current plan. */}
      {sub.pendingTier && sub.pendingEffectiveAt && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" strokeWidth={2} />
          <div className="flex-1">
            <p className="text-sm font-medium">
              Changing to {TIER_LABELS[sub.pendingTier] ?? sub.pendingTier} on{' '}
              {new Date(sub.pendingEffectiveAt).toLocaleDateString()}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              You'll keep your current {tierLabel} plan and all its features until then — you already paid for this cycle. Cancel anytime to stay on {tierLabel}.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={cancelPendingChange}
              className="mt-3"
            >
              Cancel scheduled change
            </Button>
          </div>
        </div>
      )}

      {/* Payment method + Usage — side-by-side on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6">
        {/* Payment method (2 cols) */}
        <div className="lg:col-span-2 rounded-xl border bg-card p-5 sm:p-6 card-inset flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Payment method</p>
            {paymentMethod && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-600 dark:text-green-400">
                <ShieldCheck className="h-3 w-3" />
                Secured by Stripe
              </span>
            )}
          </div>

          {paymentMethod ? (
            <>
              {/* Mini card preview */}
              <div className="relative rounded-xl bg-gradient-to-br from-foreground/90 to-foreground p-4 text-background shadow-md mb-4 overflow-hidden">
                <div className="pointer-events-none absolute -top-12 -right-8 h-32 w-32 rounded-full bg-brand-periwinkle/30 blur-2xl" aria-hidden="true" />
                <div className="relative flex items-center justify-between mb-6">
                  <span className="text-[10px] font-semibold uppercase tracking-widest opacity-70">{cardBrandLabel(paymentMethod.brand)}</span>
                  <CreditCard className="h-4 w-4 opacity-70" />
                </div>
                <p className="relative font-mono text-sm tracking-wider mb-1">
                  •••• •••• •••• {paymentMethod.last4}
                </p>
                <p className="relative text-[10px] opacity-70 uppercase tracking-wider">
                  Exp {String(paymentMethod.expMonth).padStart(2, '0')}/{String(paymentMethod.expYear).slice(-2)}
                </p>
              </div>
              <Button variant="outline" onClick={() => setUpdateCardOpen(true)} className="w-full mt-auto">
                Update card
              </Button>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center text-center flex-1 py-2">
              <div className="relative h-14 w-20 rounded-lg border-2 border-dashed border-border bg-muted/40 flex items-center justify-center mb-3">
                <CreditCard className="h-5 w-5 text-muted-foreground/60" />
              </div>
              <p className="text-sm font-semibold text-foreground">
                {isDemoActive ? 'Demo period — no billing yet' : 'No card on file'}
              </p>
              <p className="text-xs text-muted-foreground mt-1 max-w-[240px]">
                {needsSubscribe
                  ? 'Ready to keep going? Browse our packages to find the right fit for your firm.'
                  : 'Add a payment method to keep coverage active past your trial.'}
              </p>
              <Button
                onClick={() => (needsSubscribe ? openPlans() : setUpdateCardOpen(true))}
                className="mt-4 bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90 w-full"
              >
                {needsSubscribe ? 'See packages to subscribe' : 'Add card'}
              </Button>
            </div>
          )}
        </div>

        {/* Usage (3 cols) */}
        <div className="lg:col-span-3 rounded-xl border bg-card p-5 sm:p-6 card-inset">
          <div className="flex items-center justify-between mb-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Usage this period</p>
            {usage?.period && (
              <span className="text-[10px] text-muted-foreground/70">
                {new Date(usage.period.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                {' – '}
                {new Date(new Date(usage.period.end).getTime() - 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>

          <div className="space-y-5">
            <UsageRow
              label="Calls"
              icon={<Phone className="h-3.5 w-3.5 text-brand-periwinkle" />}
              iconBg="bg-brand-periwinkle/15"
              barClass="bg-gradient-to-r from-brand-royal to-brand-periwinkle"
              used={usage?.calls.used ?? null}
              included={usage?.calls.included ?? null}
              notIncludedHint="Not on Associate"
            />
            <UsageRow
              label="Chat messages"
              icon={<MessageCircle className="h-3.5 w-3.5 text-violet-500" />}
              iconBg="bg-violet-500/15"
              barClass="bg-gradient-to-r from-violet-500 to-violet-400"
              used={usage?.messages.used ?? null}
              included={usage?.messages.included ?? null}
            />
          </div>
        </div>
      </div>

      {/* Call Packs — hidden unless a card is on file. Buying charges the
          saved card immediately, and firing the buy endpoint with no default
          PM returns "No default payment method" which surfaces as a scary red
          toast. The "add card first" guard here prevents that dead-end click. */}
      <div className="rounded-xl border bg-card p-5 sm:p-6 card-inset">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Phone className="h-4 w-4 text-brand-periwinkle" />
              <h3 className="text-base font-semibold">Top up with Call Packs</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Need more calls this month? Buy a pack anytime — they roll into your current bucket instantly.
            </p>
          </div>
        </div>
        {paymentMethod ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
            {visiblePacks.map((pack) => {
              // Best per-call price across the grid (excludes the test pack
              // since $0 / 1 = 0 would always win).
              const realPacks = visiblePacks.filter(p => !p.testOnly)
              const bestPack = realPacks.reduce(
                (best, p) => p.price / p.calls < best.price / best.calls ? p : best,
                realPacks[0],
              )
              const isBestValue = pack.slug === bestPack.slug
              const isLoading = packBuying === pack.slug
              return (
                <button
                  key={pack.slug}
                  type="button"
                  disabled={packBuying !== null}
                  onClick={() => setPendingPack(pack)}
                  className={`group relative rounded-xl border p-3 sm:p-4 text-left transition-all disabled:opacity-50 ${
                    isBestValue
                      ? 'border-brand-periwinkle/50 bg-gradient-to-br from-brand-periwinkle/5 to-transparent hover:border-brand-periwinkle hover:shadow-md hover:shadow-brand-periwinkle/15'
                      : 'border-border bg-background hover:border-brand-periwinkle/40 hover:bg-accent/30 hover:-translate-y-0.5'
                  }`}
                >
                  {isBestValue && (
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider shadow-sm">
                      <Sparkles className="h-2.5 w-2.5" />
                      Best value
                    </span>
                  )}
                  <p className="text-xl sm:text-2xl font-heading font-bold">{pack.calls}</p>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground -mt-0.5">calls</p>
                  <div className="mt-3 pt-3 border-t border-border/60">
                    <p className="text-sm font-semibold tabular-nums">${pack.price}</p>
                    <p className="text-[11px] text-muted-foreground tabular-nums">
                      ${(pack.price / pack.calls).toFixed(2)}/call
                    </p>
                  </div>
                  {isLoading && (
                    <div className="absolute inset-0 rounded-xl bg-card/80 flex items-center justify-center">
                      <Loader2 className="h-4 w-4 animate-spin text-brand-periwinkle" />
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-border bg-muted/30 p-5 flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
              <CreditCard className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold">
                {needsSubscribe ? 'Subscribe to unlock call packs' : 'Add a card to buy call packs'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {needsSubscribe
                  ? 'Call packs are part of the paid plan. Subscribe and the grid unlocks.'
                  : 'Packs charge instantly to the card on file. Add one to unlock the pricing grid.'}
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => (needsSubscribe ? openPlans() : setUpdateCardOpen(true))}
                className="mt-3"
              >
                {needsSubscribe ? 'See packages' : 'Add card'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Invoices */}
      <div className="rounded-xl border bg-card p-5 sm:p-6 card-inset">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-base font-semibold">Invoices</h3>
          </div>
          <span className="text-xs text-muted-foreground">PDFs available for your records</span>
        </div>
        <InvoicesList />
      </div>

      {/* Modals (embedded — no Stripe-hosted redirects) */}
      <UpdatePaymentMethodModal
        open={updateCardOpen}
        onOpenChange={setUpdateCardOpen}
        onSuccess={() => {
          toast({ title: 'Card updated', description: 'Your new payment method is on file.' })
          loadAll()
        }}
      />

      {currentPeriodEnd && sub.stripeSubscriptionId && (
        <CancelSubscriptionDialog
          open={cancelOpen}
          onOpenChange={setCancelOpen}
          currentPeriodEnd={currentPeriodEnd}
          tierLabel={tierLabel}
          onCancelled={loadAll}
        />
      )}

      {/* Mobile-only popups. openPlans() triggers these when window width
          is ≤640px; on wider screens it routes to /settings/billing/plans
          instead and these stay closed. */}
      <ChangePlanModal
        open={changePlanOpen}
        onOpenChange={setChangePlanOpen}
        currentTier={sub.tier}
        currentInterval="monthly"
        onPlanChanged={() => {
          setChangePlanOpen(false)
          loadAll()
        }}
      />

      <ConvertDemoDialog
        open={convertOpen}
        onOpenChange={setConvertOpen}
        tier={(sub.tier as 'associate' | 'partner' | 'principal') || 'partner'}
        onSuccess={() => {
          setConvertOpen(false)
          toast({ title: 'Welcome aboard', description: 'Your subscription is active.' })
          loadAll()
        }}
      />

      {/* Call pack purchase confirmation. The charge is off-session and
          immediate, so we never fire it on the same click that picks the
          pack — always require an explicit Confirm step. */}
      <Dialog
        open={pendingPack !== null}
        onOpenChange={(open) => {
          if (!open && packBuying === null) setPendingPack(null)
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{pendingPack?.testOnly ? 'Confirm test purchase' : 'Confirm purchase'}</DialogTitle>
            <DialogDescription>
              {pendingPack?.testOnly
                ? 'This is a $0 dry-run — no charge will be placed. Verifies the confirmation flow end-to-end.'
                : 'This charge will be placed on your card immediately.'}
            </DialogDescription>
          </DialogHeader>

          {pendingPack && (
            <div className="space-y-4 py-2">
              <div className="rounded-lg border bg-muted/30 p-4">
                <div className="flex items-baseline justify-between">
                  <div>
                    <p className="text-2xl font-heading font-bold tabular-nums">
                      {pendingPack.calls} {pendingPack.calls === 1 ? 'call' : 'calls'}
                      {pendingPack.testOnly && (
                        <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-amber-600 align-middle">test</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground tabular-nums">
                      {pendingPack.price === 0
                        ? 'No charge'
                        : `$${(pendingPack.price / pendingPack.calls).toFixed(2)}/call`}
                    </p>
                  </div>
                  <p className="text-xl font-semibold tabular-nums">
                    ${pendingPack.price}
                  </p>
                </div>
              </div>

              {paymentMethod && !pendingPack.testOnly && (
                <div className="flex items-center gap-3 rounded-lg border p-3 text-sm">
                  <CreditCard className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">
                      {cardBrandLabel(paymentMethod.brand)} ending in {paymentMethod.last4}
                    </p>
                    <p className="text-xs text-muted-foreground tabular-nums">
                      Expires {String(paymentMethod.expMonth).padStart(2, '0')}/{String(paymentMethod.expYear).slice(-2)}
                    </p>
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                {pendingPack.testOnly
                  ? 'Use this to verify the confirmation dialog is wired correctly without spending real money.'
                  : "Calls roll into your current bucket instantly and don't expire while your subscription is active."}
              </p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setPendingPack(null)}
              disabled={packBuying !== null}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!pendingPack) return
                const slug = pendingPack.slug
                await buyCallPack(slug)
                setPendingPack(null)
              }}
              disabled={packBuying !== null}
            >
              {packBuying ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {pendingPack?.testOnly ? 'Verifying...' : 'Charging...'}
                </>
              ) : pendingPack?.testOnly ? (
                <>Run test (no charge)</>
              ) : (
                <>Charge ${pendingPack?.price ?? 0}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

interface UsageRowProps {
  label: string
  icon: React.ReactNode
  iconBg: string
  barClass: string
  // null = still loading. included: null = unlimited, 0 = not on this tier.
  used: number | null
  included: number | null
  notIncludedHint?: string
}

function UsageRow({ label, icon, iconBg, barClass, used, included, notIncludedHint }: UsageRowProps) {
  const isLoading = used === null
  const isUnlimited = !isLoading && included === null
  const isNotIncluded = !isLoading && included === 0
  const hasMeter = !isLoading && typeof included === 'number' && included > 0
  const pct = hasMeter ? Math.min(100, Math.round((used! / included!) * 100)) : 0
  const overage = hasMeter && used! > included!

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={`h-7 w-7 rounded-lg ${iconBg} flex items-center justify-center`}>
            {icon}
          </div>
          <span className="text-sm font-medium">{label}</span>
        </div>
        <div className="text-right">
          {isLoading && (
            <span className="text-base font-bold tabular-nums text-muted-foreground/60">—</span>
          )}
          {isUnlimited && (
            <>
              <span className="text-base font-bold tabular-nums">{used!.toLocaleString()}</span>
              <span className="text-xs text-muted-foreground ml-1.5">used · unlimited</span>
            </>
          )}
          {isNotIncluded && (
            <span className="text-xs text-muted-foreground italic">{notIncludedHint ?? 'Not included'}</span>
          )}
          {hasMeter && (
            <>
              <span className={`text-base font-bold tabular-nums ${overage ? 'text-amber-600' : ''}`}>
                {used!.toLocaleString()}
              </span>
              <span className="text-xs text-muted-foreground mx-1.5">/</span>
              <span className="text-xs tabular-nums text-muted-foreground">{included!.toLocaleString()}</span>
            </>
          )}
        </div>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        {hasMeter && (
          <div
            className={`h-full rounded-full ${overage ? 'bg-amber-500' : barClass}`}
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
    </div>
  )
}
